import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../theme';
import { useOwnerDashboard } from '../../../hooks/venue/useOwnerDashboard';
import { apiRequest } from '../../../utils/apiUtils';
import { Plus, Edit2, Trash2, Eye, MapPin, Building2 } from 'lucide-react';
import { formatPrice } from '../../../utils/venueHelpers';
import { OwnerVenue } from '../../../hooks/venue/useOwnerDashboard';

const ManageVenuesPage: React.FC = () => {
  const navigate = useNavigate();
  const { venues, loading, error, fetchVenues } = useOwnerDashboard();
  const [deletingVenueId, setDeletingVenueId] = useState<string | null>(null);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleDelete = async (venueId: string, venueName: string) => {
    if (deletingVenueId !== venueId) {
      setDeletingVenueId(venueId);
      return;
    }

    const response = await apiRequest(`/venues/${venueId}`, {
      method: 'DELETE',
    });

    if (response.data) {
      await fetchVenues();
      setDeletingVenueId(null);
    }
  };

  const getStatusBadge = (
    status: string,
    isActive: boolean
  ): { label: string; color: string } => {
    if (!isActive) {
      return { label: 'Inactiva', color: theme.colors.textSecondary };
    }

    switch (status) {
      case 'ACTIVE':
        return { label: 'Activa', color: theme.colors.primary };
      case 'PENDING_APPROVAL':
        return { label: 'Pendiente Aprobación', color: theme.colors.warning };
      case 'SUSPENDED':
        return { label: 'Suspendida', color: theme.colors.danger };
      case 'REJECTED':
        return { label: 'Rechazada', color: theme.colors.danger };
      default:
        return { label: status, color: theme.colors.textSecondary };
    }
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const breadcrumbStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const breadcrumbLinkStyle: React.CSSProperties = {
    color: theme.colors.primary,
    cursor: 'pointer',
    textDecoration: 'none',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const createButtonStyle: React.CSSProperties = {
    padding: '12px 24px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
    color: theme.colors.danger,
  };

  const venuesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '24px',
  };

  const venueCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '20px',
    border: `2px solid ${theme.colors.border}`,
    transition: 'border-color 0.2s',
    cursor: 'pointer',
  };

  const venueHeaderStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const venueNameStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontFamily: theme.fonts.heading,
  };

  const venueLocationStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '8px',
  };

  const badgeStyle = (color: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: `${color}20`,
    color: color,
    borderRadius: '12px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
  });

  const statsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginTop: '16px',
    marginBottom: '16px',
  };

  const statItemStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    padding: '12px',
    borderRadius: theme.borderRadius.input,
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '4px',
  };

  const statValueStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const actionsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.danger,
    color: '#FFF',
  };

  const confirmDeleteStyle: React.CSSProperties = {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.input,
    border: `2px solid ${theme.colors.danger}`,
  };

  const confirmTextStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const confirmActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const emptyMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '24px',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={breadcrumbStyle}>
          <a
            style={breadcrumbLinkStyle}
            onClick={() => navigate('/owner/dashboard')}
          >
            Dashboard
          </a>
          <span>/</span>
          <span>Mis Canchas</span>
        </div>
        <div style={titleRowStyle}>
          <div>
            <h1 style={titleStyle}>MIS CANCHAS</h1>
            <p style={subtitleStyle}>
              Gestioná todas tus canchas deportivas
            </p>
          </div>
          <button
            style={createButtonStyle}
            onClick={() => navigate('/owner/venues/create')}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#00D96F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary;
            }}
          >
            <Plus size={20} />
            Crear cancha
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && <div style={loadingStyle}>Cargando canchas...</div>}

      {/* Error State */}
      {error && !loading && <div style={errorStyle}>{error}</div>}

      {/* Venues Grid */}
      {!loading && !error && (
        <>
          {venues.length === 0 ? (
            <div style={emptyStateStyle}>
              <div style={emptyTitleStyle}>No tenés canchas aún</div>
              <div style={emptyMessageStyle}>
                Creá tu primera cancha para empezar a recibir reservas
              </div>
              <button
                style={primaryButtonStyle}
                onClick={() => navigate('/owner/venues/create')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#00D96F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.primary;
                }}
              >
                <Plus size={16} />
                Crear mi primera cancha
              </button>
            </div>
          ) : (
            <div style={venuesGridStyle}>
              {venues.map((venue) => {
                const statusBadge = getStatusBadge(venue.status, venue.isActive);

                return (
                  <div key={venue.id} style={venueCardStyle}>
                    {/* Header */}
                    <div style={venueHeaderStyle}>
                      <h3 style={venueNameStyle}>{venue.name}</h3>
                      <div style={venueLocationStyle}>
                        <MapPin size={14} />
                        <span>
                          {venue.city}, {venue.province}
                        </span>
                      </div>
                      <span style={badgeStyle(statusBadge.color)}>
                        {statusBadge.label}
                      </span>
                    </div>

                    {/* Stats */}
                    <div style={statsGridStyle}>
                      <div style={statItemStyle}>
                        <div style={statLabelStyle}>Reservas totales</div>
                        <div style={statValueStyle}>
                          {venue.stats?.totalBookings || 0}
                        </div>
                      </div>
                      <div style={statItemStyle}>
                        <div style={statLabelStyle}>Ingresos</div>
                        <div style={statValueStyle}>
                          {formatPrice(venue.stats?.totalRevenue || 0)}
                        </div>
                      </div>
                      <div style={statItemStyle}>
                        <div style={statLabelStyle}>Pendientes</div>
                        <div style={statValueStyle}>
                          {venue.stats?.pendingBookings || 0}
                        </div>
                      </div>
                      <div style={statItemStyle}>
                        <div style={statLabelStyle}>Reviews</div>
                        <div style={statValueStyle}>
                          {venue.stats?.reviewCount || 0}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {deletingVenueId !== venue.id ? (
                      <div style={actionsContainerStyle}>
                        <button
                          style={primaryButtonStyle}
                          onClick={() =>
                            navigate(`/owner/venues/${venue.id}/edit`)
                          }
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#00D96F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              theme.colors.primary;
                          }}
                        >
                          <Edit2 size={14} />
                          Editar
                        </button>
                        <button
                          style={secondaryButtonStyle}
                          onClick={() => navigate(`/venues/${venue.id}`)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              theme.colors.background;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Eye size={14} />
                          Ver
                        </button>
                        <button
                          style={secondaryButtonStyle}
                          onClick={() => setDeletingVenueId(venue.id)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              theme.colors.background;
                            e.currentTarget.style.borderColor = theme.colors.danger;
                            e.currentTarget.style.color = theme.colors.danger;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = theme.colors.border;
                            e.currentTarget.style.color = theme.colors.text;
                          }}
                        >
                          <Trash2 size={14} />
                          Eliminar
                        </button>
                      </div>
                    ) : (
                      <div style={confirmDeleteStyle}>
                        <div style={confirmTextStyle}>
                          ¿Estás seguro que querés eliminar "{venue.name}"? Esta
                          acción no se puede deshacer.
                        </div>
                        <div style={confirmActionsStyle}>
                          <button
                            style={dangerButtonStyle}
                            onClick={() => handleDelete(venue.id, venue.name)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#CC0000';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                theme.colors.danger;
                            }}
                          >
                            Sí, eliminar
                          </button>
                          <button
                            style={secondaryButtonStyle}
                            onClick={() => setDeletingVenueId(null)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                theme.colors.background;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageVenuesPage;
